import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { toast } from '@/hooks/use-toast';

export type GoalStatus = 'active' | 'completed' | 'paused';

export interface FinancialGoal {
  id: string;
  user_id: string;
  name: string;
  target_amount: number;
  current_amount: number;
  deadline: string | null;
  priority: number;
  status: GoalStatus;
  created_at: string;
}

export interface CreateGoalInput {
  name: string;
  target_amount: number;
  current_amount?: number;
  deadline?: string;
  priority?: number;
  status?: GoalStatus;
}

export function useGoals() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data: goals = [], isLoading, error } = useQuery({
    queryKey: ['goals', user?.id],
    queryFn: async () => {
      if (!user) return [];
      
      const { data, error } = await supabase
        .from('financial_goals')
        .select('*')
        .eq('user_id', user.id)
        .order('priority', { ascending: true });

      if (error) throw error;
      return data as FinancialGoal[];
    },
    enabled: !!user,
  });

  const createGoal = useMutation({
    mutationFn: async (input: CreateGoalInput) => {
      if (!user) throw new Error('No user');
      
      const { data, error } = await supabase
        .from('financial_goals')
        .insert({
          user_id: user.id,
          ...input,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['goals'] });
      queryClient.invalidateQueries({ queryKey: ['monthly-summary'] });
      toast({ title: 'Meta creada', description: 'Tu meta financiera se ha registrado correctamente.' });
    },
    onError: () => {
      toast({ title: 'Error', description: 'No se pudo crear la meta.', variant: 'destructive' });
    },
  });

  const updateGoal = useMutation({
    mutationFn: async ({ id, ...input }: Partial<CreateGoalInput> & { id: string }) => {
      const { data, error } = await supabase
        .from('financial_goals')
        .update(input)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['goals'] });
      queryClient.invalidateQueries({ queryKey: ['monthly-summary'] });
      toast({ title: 'Meta actualizada' });
    },
    onError: () => {
      toast({ title: 'Error', description: 'No se pudo actualizar la meta.', variant: 'destructive' });
    },
  });

  const deleteGoal = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('financial_goals')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['goals'] });
      queryClient.invalidateQueries({ queryKey: ['monthly-summary'] });
      toast({ title: 'Meta eliminada' });
    },
    onError: () => {
      toast({ title: 'Error', description: 'No se pudo eliminar la meta.', variant: 'destructive' });
    },
  });

  const bulkCreateGoals = useMutation({
    mutationFn: async (inputs: CreateGoalInput[]) => {
      if (!user) throw new Error('No user');
      
      const goalsToInsert = inputs.map(input => ({
        user_id: user.id,
        name: input.name,
        target_amount: input.target_amount,
        current_amount: input.current_amount || 0,
        deadline: input.deadline || null,
        priority: input.priority || 3,
        status: input.status || 'active',
      }));

      const { data, error } = await supabase
        .from('financial_goals')
        .insert(goalsToInsert)
        .select();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['goals'] });
      queryClient.invalidateQueries({ queryKey: ['monthly-summary'] });
    },
    onError: (error) => {
      console.error('Bulk import error:', error);
      toast({ title: 'Error', description: 'No se pudieron importar las metas.', variant: 'destructive' });
    },
  });

  return {
    goals,
    isLoading,
    error,
    createGoal,
    updateGoal,
    deleteGoal,
    bulkCreateGoals,
  };
}
